import React, { useEffect, useState } from 'react';
import {
  getVersionInfo,
  getShortVersion,
  ComponentVersions,
  fetchBackendVersion,
} from '../utils/version';

interface VersionDisplayProps {
  position?: 'header' | 'footer' | 'sidebar';
  showDetails?: boolean;
  componentName?: keyof typeof ComponentVersions;
  /** 省略時は window.fetch。App から渡すと認証付き apiFetch を使える. */
  apiFetch?: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
}

const VersionDisplay: React.FC<VersionDisplayProps> = ({
  position = 'footer',
  showDetails = false,
  componentName,
  apiFetch,
}) => {
  const [showDetailedInfo, setShowDetailedInfo] = useState(false);
  const versionInfo = getVersionInfo();
  const [label, setLabel] = useState(getShortVersion());

  useEffect(() => {
    let cancelled = false;
    // ビルド時 env が空なら API から補完（graceful）
    if (versionInfo.gitCommit && versionInfo.deployDatetime) {
      return;
    }
    fetchBackendVersion(apiFetch).then((remote) => {
      if (cancelled || !remote) return;
      const commit = (remote.git_commit || versionInfo.gitCommit || '').slice(0, 7);
      const deployed =
        remote.deploy_datetime || versionInfo.deployDatetime || versionInfo.buildTime || '';
      const parts: string[] = [];
      if (commit) parts.push(commit);
      else parts.push(`v${versionInfo.system}`);
      if (deployed) parts.push(deployed);
      if (parts.length) setLabel(parts.join(' · '));
    });
    return () => {
      cancelled = true;
    };
  }, [apiFetch, versionInfo]);

  const getPositionStyles = () => {
    switch (position) {
      case 'header':
        return {
          position: 'absolute' as const,
          top: '8px',
          right: '16px',
          fontSize: '11px',
          color: '#6c757d',
          zIndex: 1000,
        };
      case 'sidebar':
        return {
          position: 'absolute' as const,
          bottom: '8px',
          left: '8px',
          fontSize: '10px',
          color: '#6c757d',
        };
      case 'footer':
      default:
        return {
          position: 'fixed' as const,
          bottom: '8px',
          right: '16px',
          fontSize: '11px',
          color: '#6c757d',
          zIndex: 1000,
        };
    }
  };

  const handleClick = () => {
    if (showDetails) {
      setShowDetailedInfo(!showDetailedInfo);
    }
  };

  return (
    <div
      style={{
        ...getPositionStyles(),
        cursor: showDetails ? 'pointer' : 'default',
        backgroundColor: showDetailedInfo ? 'rgba(255,255,255,0.9)' : 'transparent',
        padding: showDetailedInfo ? '8px' : '2px',
        borderRadius: '4px',
        border: showDetailedInfo ? '1px solid #dee2e6' : 'none',
      }}
      onClick={handleClick}
      title={showDetails ? 'クリックで詳細表示' : label}
    >
      {!showDetailedInfo ? (
        <div>
          {label}
          {componentName && (
            <span style={{ marginLeft: '4px', fontSize: '9px' }}>
              ({componentName}: {ComponentVersions[componentName]})
            </span>
          )}
        </div>
      ) : (
        <div style={{ fontSize: '10px', lineHeight: '1.3' }}>
          <div>
            <strong>システム:</strong> {versionInfo.system}
          </div>
          <div>
            <strong>フロントエンド:</strong> {versionInfo.frontend}
          </div>
          <div>
            <strong>バックエンド:</strong> {versionInfo.backend}
          </div>
          <div>
            <strong>データベース:</strong> {versionInfo.database}
          </div>
          <div>
            <strong>デプロイ:</strong>{' '}
            {versionInfo.deployDatetime || versionInfo.buildTime || '—'}
          </div>
          {componentName && (
            <div>
              <strong>{componentName}:</strong> {ComponentVersions[componentName]}
            </div>
          )}
          {(versionInfo.gitCommit || label) && (
            <div>
              <strong>Git:</strong> {(versionInfo.gitCommit || label).substring(0, 7)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VersionDisplay;
